import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { API_URL } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import type { 
  Firm, Deal, DealStage, Signal, SignalType,
  ContactSummary, OutreachMessage, InvestorMatch,
  AlumniGraph, WarmIntro, SearchResult
} from '@/types/backr';
import type { Contact } from '@/types/contacts';

// ============================================
// API HELPERS
// ============================================

async function fetchWithAuth(url: string, options: RequestInit = {}, token: string) {
  const response = await fetch(url, {
    ...options,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

// ============================================
// USE FIRMS
// ============================================

export function useFirms() {
  const { token } = useAuth();
  const { toast } = useToast();
  const [firms, setFirms] = useState<Firm[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const searchFirms = useCallback(async (query: string) => {
    if (!token) return;
    setIsLoading(true);
    try {
      const data = await fetchWithAuth(
        `${API_URL}/api/backr/firms?search=${encodeURIComponent(query)}`,
        {},
        token
      );
      setFirms(data.firms);
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to search firms',
      });
    } finally {
      setIsLoading(false);
    }
  }, [token, toast]);

  const getFirm = useCallback(async (id: string): Promise<Firm | null> => {
    if (!token) return null;
    try {
      return await fetchWithAuth(`${API_URL}/api/backr/firms/${id}`, {}, token);
    } catch (err) {
      return null;
    }
  }, [token]);

  return { firms, isLoading, searchFirms, getFirm };
}

// ============================================
// USE DEALS
// ============================================

export function useDeals() {
  const { token } = useAuth();
  const { toast } = useToast();
  const [deals, setDeals] = useState<Deal[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchDeals = useCallback(async (filters?: { stage?: DealStage; status?: string }) => {
    if (!token) return;
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters?.stage) params.set('stage', filters.stage);
      if (filters?.status) params.set('status', filters.status);
      
      const data = await fetchWithAuth(
        `${API_URL}/api/backr/deals?${params}`,
        {},
        token
      );
      setDeals(data.deals);
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to fetch deals',
      });
    } finally {
      setIsLoading(false);
    }
  }, [token, toast]);

  const createDeal = useCallback(async (dealData: Partial<Deal>): Promise<Deal | null> => {
    if (!token) return null;
    try {
      const data = await fetchWithAuth(
        `${API_URL}/api/backr/deals`,
        { method: 'POST', body: JSON.stringify(dealData) },
        token
      );
      toast({ title: 'Success', description: 'Deal created' });
      await fetchDeals();
      return data;
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to create deal',
      });
      return null;
    }
  }, [token, toast, fetchDeals]);

  const updateStage = useCallback(async (dealId: string, stage: DealStage) => {
    if (!token) return;
    try {
      await fetchWithAuth(
        `${API_URL}/api/backr/deals/${dealId}/stage`,
        { method: 'PUT', body: JSON.stringify({ stage }) },
        token
      );
      setDeals(prev => prev.map(d => d.id === dealId ? { ...d, stage } : d));
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to update stage',
      });
    }
  }, [token, toast]);

  const addContactToDeal = useCallback(async (dealId: string, contactId: string) => {
    if (!token) return;
    try {
      await fetchWithAuth(
        `${API_URL}/api/backr/deals/${dealId}/contacts`,
        { method: 'POST', body: JSON.stringify({ contact_id: contactId }) },
        token
      );
      toast({ title: 'Success', description: 'Contact added to deal' });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to add contact',
      });
    }
  }, [token, toast]);

  const dealsByStage = useMemo(() => {
    const stages: DealStage[] = ['targeted', 'contacted', 'replied', 'meeting_booked', 'in_diligence', 'invested', 'passed'];
    const grouped: Record<DealStage, Deal[]> = {
      targeted: [], contacted: [], replied: [], meeting_booked: [],
      in_diligence: [], invested: [], passed: []
    };
    deals.forEach(deal => {
      if (grouped[deal.stage]) {
        grouped[deal.stage].push(deal);
      }
    });
    return grouped;
  }, [deals]);

  return {
    deals,
    dealsByStage,
    isLoading,
    fetchDeals,
    createDeal,
    updateStage,
    addContactToDeal,
  };
}

// ============================================
// USE SIGNALS
// ============================================

export function useSignals() {
  const { token } = useAuth();
  const { toast } = useToast();
  const [signals, setSignals] = useState<Signal[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchSignals = useCallback(async (filters?: { type?: SignalType; unread_only?: boolean }) => {
    if (!token) return;
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters?.type) params.set('type', filters.type);
      if (filters?.unread_only) params.set('unread_only', 'true');
      
      const data = await fetchWithAuth(
        `${API_URL}/api/backr/signals?${params}`,
        {},
        token
      );
      setSignals(data.signals);
      setUnreadCount(data.signals.filter((s: Signal) => !s.is_read).length);
    } catch (err) {
      console.error('Failed to fetch signals:', err);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  const markAsRead = useCallback(async (signalId: string) => {
    if (!token) return;
    try {
      await fetchWithAuth(
        `${API_URL}/api/backr/signals/${signalId}/read`,
        { method: 'PUT' },
        token
      );
      setSignals(prev => prev.map(s => s.id === signalId ? { ...s, is_read: true } : s));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  }, [token]);

  return { signals, unreadCount, isLoading, fetchSignals, markAsRead };
}

// ============================================
// USE AI
// ============================================

export function useAI() {
  const { token } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const summarizeContact = useCallback(async (contactId: string): Promise<ContactSummary | null> => {
    if (!token) return null;
    setIsLoading(true);
    try {
      const data = await fetchWithAuth(
        `${API_URL}/api/backr/ai/summarize-contact`,
        { method: 'POST', body: JSON.stringify({ contact_id: contactId }) },
        token
      );
      return data.summary;
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to generate summary',
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [token, toast]);

  const generateOutreach = useCallback(async (
    contactId: string, 
    options?: { tone?: 'professional' | 'casual' | 'direct'; context?: string }
  ): Promise<OutreachMessage | null> => {
    if (!token) return null;
    setIsLoading(true);
    try {
      const data = await fetchWithAuth(
        `${API_URL}/api/backr/ai/generate-outreach`,
        { 
          method: 'POST', 
          body: JSON.stringify({ 
            contact_id: contactId, 
            tone: options?.tone || 'professional',
            context: options?.context 
          }) 
        },
        token
      );
      return data;
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to generate outreach',
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [token, toast]);

  const suggestInvestors = useCallback(async (criteria: {
    company_description: string;
    stage?: string;
    industry?: string;
    raise_amount?: number;
  }): Promise<InvestorMatch[]> => {
    if (!token) return [];
    setIsLoading(true);
    try {
      const data = await fetchWithAuth(
        `${API_URL}/api/backr/ai/suggest-investors`,
        { method: 'POST', body: JSON.stringify(criteria) },
        token
      );
      return data.suggestions;
    } catch (err) {
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  return {
    isLoading,
    summarizeContact,
    generateOutreach,
    suggestInvestors,
  };
}

// ============================================
// USE BACKR FEATURES
// ============================================

export function useRaiseBeaconFeatures() {
  const { token } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const getAlumniGraph = useCallback(async (school?: string): Promise<AlumniGraph | null> => {
    if (!token) return null;
    setIsLoading(true);
    try {
      const params = school ? `?school=${encodeURIComponent(school)}` : '';
      return await fetchWithAuth(
        `${API_URL}/api/backr/alumni-graph${params}`,
        {},
        token
      );
    } catch (err) {
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  const getWarmIntros = useCallback(async (targetContactId: string): Promise<WarmIntro | null> => {
    if (!token) return null;
    setIsLoading(true);
    try {
      return await fetchWithAuth(
        `${API_URL}/api/backr/warm-intros?target_contact_id=${targetContactId}`,
        {},
        token
      );
    } catch (err) {
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  return {
    isLoading,
    getAlumniGraph,
    getWarmIntros,
  };
}

// ============================================
// USE GLOBAL SEARCH
// ============================================

export function useGlobalSearch() {
  const { token } = useAuth();
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const search = useCallback(async (query: string, filters?: Record<string, unknown>) => {
    if (!token || query.length < 2) {
      setResults([]);
      return;
    }
    setIsSearching(true);
    try {
      const params = new URLSearchParams({ q: query });
      const data = await fetchWithAuth(
        `${API_URL}/api/backr/search?${params}`,
        {},
        token
      );
      setResults(data.results);
    } catch (err) {
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [token]);

  return { results, isSearching, search };
}
